import React, { memo, useCallback } from 'react';
import { useEffect, useRef, useState } from 'react';
import hljs from 'highlight.js';
import katex from 'katex';
import Quill from 'quill';
import { TOOLBAR_OPTIONS, DIRECTION } from '../constants/constants';
window.katex = katex;

// Interface for Quill toolbar module
interface QuillToolbar {
  addHandler: (format: string, handler: () => void) => void;
  container?: HTMLElement;
}
const theme = 'snow';
const modules = {
  syntax: {
    hljs,
    // Fix for: Syntax module requires highlight.js. Please include the library on the page before Quill.
    Highlight: (text: string) => hljs.highlightAuto(text).value
  },
  toolbar: TOOLBAR_OPTIONS,
  clipboard: {
    matchVisual: false
  }
};
type TextEventT = ({
  html,
  text
}: {
  html: string;
  text: string;
}) => void;
type DirectionT = (typeof DIRECTION)[keyof typeof DIRECTION];
type PropsT = {
  defaultValue?: string;
  readOnly?: boolean;
  onChange: TextEventT;
  onBlur?: TextEventT;
  placeholder?: string;
  direction?: DirectionT;
  bounds: string | HTMLElement;
  enableAutoFocusForRTE?: boolean;
  config?: {
    disablePaste?: boolean;
  };
};
const problematicKeys = ['-'];
const ReactQuill = ({
  defaultValue,
  readOnly,
  onChange,
  onBlur,
  placeholder,
  direction,
  bounds,
  enableAutoFocusForRTE = true,
  config
}: PropsT) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [editor, setEditor] = useState<Quill | null>(null);
  const defaultValueRef = useRef<string>(defaultValue || '');
  const onChangeRef = useRef<TextEventT>(onChange);
  const onBlurRef = useRef<TextEventT | null>(onBlur || null);
  const readOnlyRef = useRef<boolean>(readOnly || false);
  const placeholderRef = useRef<string>(placeholder || '');
  const directionRef = useRef<DirectionT>(direction || DIRECTION.LTR);
  const handleInputChangeEvent = (editor: Quill) => {
    onChangeRef.current({
      html: editor.getSemanticHTML(),
      text: editor.getText()
    });
  };
  const handleInputBlurEvent = (editor: Quill) => {
    onBlurRef.current?.({
      html: editor.getSemanticHTML(),
      text: editor.getText()
    });
  };
  const handlePasteViaRightClick = useCallback((event: MouseEvent) => {
    event.preventDefault();
    alert('Pasting is disabled!');
  }, []);
  useEffect(() => {
    if (!containerRef.current) return;
    const quillInput = readOnly ? {
      modules: {
        toolbar: false
      },
      theme: 'bubble',
      readOnly: true
    } : {
      modules,
      theme,
      placeholder: placeholderRef.current,
      bounds
    };
    const editor = new Quill(containerRef.current, quillInput);

    // Override the focus method to always use preventScroll
    const originalFocus = editor.focus.bind(editor);
    editor.focus = (options?: boolean | {
      preventScroll?: boolean;
    }) => {
      if (!enableAutoFocusForRTE) {
        return;
      }
      if (typeof options === 'boolean') {
        // Legacy API: focus(force)
        return originalFocus({
          preventScroll: true
        });
      } else {
        // Modern API: focus({ preventScroll: true })
        return originalFocus({
          ...options,
          preventScroll: true
        });
      }
    };

    // Custom toolbar handlers for proper format toggling
    const toolbar = (editor.getModule('toolbar') as QuillToolbar | undefined);
    if (toolbar?.addHandler) {
      function toggleFormat(format: string) {
        const range = editor.getSelection();
        if (!range) return;
        const current = editor.getFormat(range)[format];
        editor.format(format, !current);
      }

      // Rebind toolbar handlers to ensure toggling works properly
      toolbar.addHandler('bold', () => toggleFormat('bold'));
      toolbar.addHandler('italic', () => toggleFormat('italic'));
      toolbar.addHandler('underline', () => toggleFormat('underline'));
      toolbar.addHandler('strike', () => toggleFormat('strike'));
    }
    setEditor(editor);
    return () => {
      setEditor(null);
    };
  }, [bounds, readOnly, enableAutoFocusForRTE]);
  useEffect(() => {
    if (!editor) return;
    const animationFrameId = requestAnimationFrame(() => {
      if (!enableAutoFocusForRTE || editor.hasFocus()) {
        editor.blur();
      }
    });
    if (defaultValueRef.current) {
      editor.clipboard.dangerouslyPasteHTML(defaultValueRef.current);
    }
    if (readOnlyRef.current) {
      editor.enable(!readOnlyRef.current);
    }
    if (directionRef.current) {
      editor.format('direction', directionRef.current);
      editor.format('align', directionRef.current === DIRECTION.RTL ? 'right' : 'left');
    }
    const textChangeHandler = () => handleInputChangeEvent(editor);
    editor.on(Quill.events.TEXT_CHANGE, textChangeHandler);
    const keydownHandler = (event: KeyboardEvent) => {
      if (config?.disablePaste && (event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault();
        alert('Pasting is disabled!');
        return;
      }
      if (problematicKeys.includes(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey && !event.isComposing && event.target === editor.root) {
        event.preventDefault();
        const selection = editor.getSelection();
        if (selection) {
          editor.insertText(selection.index, event.key, 'user');
          editor.setSelection(selection.index + 1, 0, 'user');
        }
      }
    };
    editor.root.addEventListener('keydown', keydownHandler);
    editor.root.addEventListener('blur', () => handleInputBlurEvent(editor));
    if (config?.disablePaste) {
      editor.root.addEventListener('contextmenu', handlePasteViaRightClick);
    }
    return () => {
      cancelAnimationFrame(animationFrameId);
      editor.off(Quill.events.TEXT_CHANGE, textChangeHandler);
      editor.root.removeEventListener('keydown', keydownHandler);
      editor.root.removeEventListener('blur', () => handleInputBlurEvent(editor));
      if (config?.disablePaste) {
        editor.root.removeEventListener('contextmenu', handlePasteViaRightClick);
      }
    };
  }, [config?.disablePaste, editor, handlePasteViaRightClick, enableAutoFocusForRTE]);
  return <div ref={containerRef}></div>;
};
export default memo(ReactQuill);